import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Megaphone, Package, MessageCircle, Phone, Send, CheckCircle2, MapPin
} from "lucide-react";
import { toast } from "sonner";
import "@/trade.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const digits = (value) => (value || "").replace(/[^0-9]/g, "");

const AdvertisePublicPage = () => {
  const { shareId } = useParams();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inquiryProduct, setInquiryProduct] = useState("all");
  const [form, setForm] = useState({ buyer_name: "", buyer_phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/advertisements/shared/${shareId}`)
      .then(res => setAd(res.data))
      .catch(e => setError(e.response?.data?.detail || "This advertisement is not available"))
      .finally(() => setLoading(false));
  }, [shareId]);

  const priceLabel = (product) => {
    if (product.show_price && product.price !== null && product.price !== undefined && product.price !== "") {
      return `${product.currency || "ETB"} ${Number(product.price).toLocaleString()}`;
    }
    return "Contact for price";
  };

  const imageUrl = (product) => `${API}/advertisements/shared/${shareId}/image/${product.id}`;
  const productInterest = (product) => `Hello ${ad.business_name}, I am interested in ${product.name} from your advertisement: ${window.location.href}`;

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!form.buyer_name.trim() || !form.buyer_phone.trim()) {
      toast.error("Please add your name and phone number");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/advertisements/shared/${shareId}/inquiries`, {
        ...form,
        product_id: inquiryProduct === "all" ? "" : inquiryProduct
      });
      setSent(true);
      setForm({ buyer_name: "", buyer_phone: "", message: "" });
      setInquiryProduct("all");
      toast.success("Your request was sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="trade-section min-h-screen flex items-center justify-center px-6">
        <div className="text-white/50" data-testid="public-ad-loading">Loading products...</div>
      </div>
    );
  }

  if (error || !ad) {
    return (
      <div className="trade-section min-h-screen flex items-center justify-center px-6">
        <div className="trade-card rounded-sm p-10 max-w-md text-center" data-testid="public-ad-error">
          <Package className="w-14 h-14 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
          <h1 className="text-2xl font-serif mb-2">Advertisement unavailable</h1>
          <p className="text-white/50 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trade-section min-h-screen trade-grid-pattern">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="trade-card rounded-sm p-6 md:p-10 mb-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 trade-btn-primary rounded-sm flex items-center justify-center">
                <Megaphone className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] trade-gold">Goods Available</p>
                <p className="text-sm text-white/55">{ad.business_name}</p>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-testid="public-ad-title">{ad.title}</h1>
            {ad.message && <p className="text-white/65 max-w-3xl whitespace-pre-wrap" data-testid="public-ad-message">{ad.message}</p>}
            <div className="flex flex-wrap gap-3 mt-6">
              {ad.whatsapp && (
                <a
                  href={`https://wa.me/${digits(ad.whatsapp)}?text=${encodeURIComponent(`Hello ${ad.business_name}, I saw your goods advertisement: ${window.location.href}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-[#10b981] text-white text-sm font-semibold hover:bg-[#059669] transition-colors"
                  data-testid="public-ad-whatsapp"
                >
                  <MessageCircle size={16} /> WhatsApp Seller
                </a>
              )}
              {ad.phone && (
                <a
                  href={`tel:${ad.phone}`}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-white/10 text-white text-sm font-semibold hover:bg-white/20 transition-colors"
                  data-testid="public-ad-call"
                >
                  <Phone size={16} /> Call Seller
                </a>
              )}
            </div>
          </div>
        </motion.header>

        <section className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg md:text-xl font-serif font-semibold">Products</h2>
            <span className="text-xs text-white/40">{(ad.products || []).length} item{(ad.products || []).length === 1 ? "" : "s"}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(ad.products || []).map((product, i) => (
              <motion.article
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="trade-card rounded-sm overflow-hidden"
                data-testid={`public-product-${product.id}`}
              >
                {product.image_path || product.image_content_type ? (
                  <img
                    src={imageUrl(product)}
                    alt={product.name}
                    className="w-full aspect-[4/3] object-cover bg-[#0a1628]"
                    data-testid={`public-product-image-${product.id}`}
                  />
                ) : (
                  <div className="w-full aspect-[4/3] bg-[#0a1628] flex items-center justify-center">
                    <Package className="w-14 h-14 trade-gold opacity-30" strokeWidth={1} />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h3 className="font-serif font-semibold text-lg truncate">{product.name}</h3>
                      {product.shipment_name && (
                        <p className="text-[11px] text-white/40 flex items-center gap-1 mt-1">
                          <MapPin size={11} /> {product.shipment_name}
                        </p>
                      )}
                    </div>
                    <span className="text-xs trade-gold font-semibold whitespace-nowrap" data-testid={`public-product-price-${product.id}`}>
                      {priceLabel(product)}
                    </span>
                  </div>
                  {product.quantity && <p className="text-xs text-white/50 mb-2">Available: {product.quantity}</p>}
                  {product.description && <p className="text-sm text-white/60 whitespace-pre-wrap mb-4">{product.description}</p>}
                  <div className="flex gap-2 mt-4">
                    {ad.whatsapp && (
                      <a
                        href={`https://wa.me/${digits(ad.whatsapp)}?text=${encodeURIComponent(productInterest(product))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-sm bg-[#10b981]/10 trade-green text-xs font-semibold hover:bg-[#10b981]/20 transition-colors"
                        data-testid={`public-product-whatsapp-${product.id}`}
                      >
                        <MessageCircle size={13} /> WhatsApp
                      </a>
                    )}
                    {ad.phone && (
                      <a
                        href={`tel:${ad.phone}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-sm bg-white/5 text-white/70 text-xs font-semibold hover:bg-white/10 transition-colors"
                        data-testid={`public-product-call-${product.id}`}
                      >
                        <Phone size={13} /> Call
                      </a>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="trade-card rounded-sm p-6 md:p-8 max-w-3xl mx-auto"
          data-testid="public-inquiry-card"
        >
          <div className="text-center mb-6">
            <p className="text-[10px] uppercase tracking-[0.3em] trade-gold mb-2">Interested?</p>
            <h2 className="text-2xl md:text-3xl font-serif font-bold">Send a Buying Request</h2>
            <p className="text-white/50 text-sm mt-2">Leave your name and phone number — the seller will contact you back.</p>
          </div>

          {sent ? (
            <div className="text-center p-8 border border-[#10b981]/20 bg-[#10b981]/5 rounded-sm" data-testid="public-inquiry-success">
              <CheckCircle2 className="w-12 h-12 trade-green mx-auto mb-3" />
              <h3 className="font-serif text-xl mb-2">Request sent</h3>
              <p className="text-white/55 text-sm mb-5">The seller received your inquiry and will contact you.</p>
              <Button onClick={() => setSent(false)} className="bg-white/10 text-white hover:bg-white/20 rounded-sm" data-testid="send-another-inquiry">
                Send Another Request
              </Button>
            </div>
          ) : (
            <form onSubmit={submitInquiry} className="space-y-4" data-testid="public-inquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">Your name *</Label>
                  <Input
                    value={form.buyer_name}
                    onChange={e => setForm(f => ({ ...f, buyer_name: e.target.value }))}
                    className="trade-input mt-1 h-12 rounded-sm"
                    data-testid="buyer-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Your phone / WhatsApp *</Label>
                  <Input
                    value={form.buyer_phone}
                    onChange={e => setForm(f => ({ ...f, buyer_phone: e.target.value }))}
                    placeholder="+251..."
                    className="trade-input mt-1 h-12 rounded-sm"
                    data-testid="buyer-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-white/60">Product</Label>
                <Select value={inquiryProduct} onValueChange={setInquiryProduct}>
                  <SelectTrigger className="trade-input mt-1 h-12 rounded-sm" data-testid="buyer-product-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#142039] border-white/10">
                    <SelectItem value="all">General inquiry</SelectItem>
                    {(ad.products || []).map(product => (
                      <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/60">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  rows={4}
                  placeholder="Quantity, delivery city, offer price, or any question..."
                  className="trade-input mt-1 rounded-sm"
                  data-testid="buyer-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="trade-btn-primary w-full h-12 rounded-sm"
                data-testid="send-inquiry-btn"
              >
                <Send size={16} className="mr-2" />
                {sending ? "Sending..." : "Send Buying Request"}
              </Button>
            </form>
          )}
        </motion.section>
      </div>
    </div>
  );
};

export default AdvertisePublicPage;
